import { debounce } from "@/utils/debounce";
import { useMap } from "@vis.gl/react-google-maps";
import { useEffect, useRef } from "react";

export const getMarkerSize = (zoom: number) => {
	const size = zoom < 12 ? zoom : zoom < 16 ? zoom * 2 : zoom * 2; // Adjust zoom level to match size mapping
	return size + "px";
};

export function useMapMarkerZoomUpdater() {
	const map = useMap();
	const rafRef = useRef<number | null>(null);

	useEffect(() => {
		if (!map) return;

		// Set initial zoom level
		const initialZoom = map.getZoom();
		if (initialZoom !== undefined) {
			// Set initial zoom for marker sizing
			document.documentElement.style.setProperty("--marker-size", getMarkerSize(initialZoom));
		}

		// Batched function to update marker size using requestAnimationFrame
		const updateMarkerSize = () => {
			// Cancel any pending animation frame
			if (rafRef.current) {
				cancelAnimationFrame(rafRef.current);
			}

			rafRef.current = requestAnimationFrame(() => {
				const currentZoom = map.getZoom();
				if (currentZoom !== undefined) {
					document.documentElement.style.setProperty("--marker-size", getMarkerSize(currentZoom));
				}
				rafRef.current = null;
			});
		};

		// Debounced version to prevent excessive calls
		const debouncedUpdateMarkerSize = debounce(updateMarkerSize, 100);

		const zoomListener = map.addListener("zoom_changed", debouncedUpdateMarkerSize);

		return () => {
			if (zoomListener) {
				zoomListener.remove();
			}
			// Cancel any pending animation frame on cleanup
			if (rafRef.current) {
				cancelAnimationFrame(rafRef.current);
			}
		};
	}, [map]);
}
